import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

interface ProblemStateData {
  new: number;
  assess_1: number;
  assess_2: number;
  root_cause_analysis: number;
  resolved: number;
  closed: number;
  cancelled: number;
}

interface ProblemStateBarChartProps {
  data: ProblemStateData;
  onBarClick?: (state: string) => void;
}

const stateLabels: Record<string, string> = {
  new: 'New',
  assess_1: 'Assess 1',
  assess_2: 'Assess 2',
  root_cause_analysis: 'RCA',
  resolved: 'Resolved',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const stateColors: Record<string, string> = {
  new: 'rgba(59, 130, 246, 0.8)',           // blue
  assess_1: 'rgba(245, 158, 11, 0.8)',      // amber
  assess_2: 'rgba(249, 115, 22, 0.8)',      // orange
  root_cause_analysis: 'rgba(147, 51, 234, 0.8)', // purple
  resolved: 'rgba(16, 185, 129, 0.8)',      // green
  closed: 'rgba(107, 114, 128, 0.8)',       // dark gray
  cancelled: 'rgba(239, 68, 68, 0.8)',      // red
};

const ProblemStateBarChart: React.FC<ProblemStateBarChartProps> = ({ data, onBarClick }) => {
  const stateKeys = Object.keys(stateLabels);
  const labels = stateKeys.map(key => stateLabels[key]);
  const values = stateKeys.map(key => data[key as keyof ProblemStateData] || 0);
  const colors = stateKeys.map(key => stateColors[key]);
  const total = values.reduce((sum, val) => sum + val, 0);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Problems',
        data: values,
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw as number;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${value} problems (${percentage}%)`;
          }
        }
      },
      datalabels: {
        display: (context: any) => context.dataset.data[context.dataIndex] > 0,
        color: '#fff',
        font: {
          weight: 'bold' as const,
          size: 11,
        },
        anchor: 'center' as const,
        align: 'center' as const,
        formatter: (value: number) => value > 0 ? value : '',
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { display: false },
        ticks: { precision: 0 }
      },
      y: {
        grid: { display: false }
      }
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index;
        onBarClick(stateKeys[index]);
      }
    },
  };

  return (
    <div id="chart-problem-state-bar" className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">State-wise Problem Count</h3>
        <span className="text-xs text-gray-500">Click bar to view details</span>
      </div>
      <div className="h-64">
        <Bar data={chartData} options={options} />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {stateKeys.map((state, index) => (
          <button
            key={state}
            onClick={() => onBarClick?.(state)}
            className="inline-flex items-center px-2 py-1 rounded text-xs font-medium hover:opacity-80 transition-opacity"
            style={{ backgroundColor: stateColors[state], color: 'white' }}
          >
            {stateLabels[state]}: {values[index]}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ProblemStateBarChart;
